import { Injectable, inject, signal } from '@angular/core';
import { StoragePrefix } from '@core/constant';
import { StorageService } from 'src/app/utils';

export type PwaInstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private _storage = inject(StorageService);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private promptReadyCallbacks = new Set<() => void>();

  canInstall = signal(false);
  isInstalled = signal(this.hasInstalledApp());

  constructor() {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.canInstall.set(true);
      this.promptReadyCallbacks.forEach((callback) => callback());
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.canInstall.set(false);
      this.markInstalled();
    });
  }

  onPromptReady(callback: () => void): () => void {
    this.promptReadyCallbacks.add(callback);

    if (this.deferredPrompt) {
      queueMicrotask(callback);
    }

    return () => this.promptReadyCallbacks.delete(callback);
  }

  async install(): Promise<PwaInstallOutcome> {
    if (!this.deferredPrompt) {
      return 'unavailable';
    }

    const prompt = this.deferredPrompt;
    try {
      await prompt.prompt();
    } catch {
      return 'unavailable';
    }

    const choiceResult = await prompt.userChoice;
    this.deferredPrompt = null;
    this.canInstall.set(false);

    if (choiceResult.outcome === 'accepted') {
      this.markInstalled();
    }

    return choiceResult.outcome;
  }

  isIosDevice(): boolean {
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
      || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  }

  isStandaloneMode(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  markInstalled() {
    this._storage.set(StoragePrefix.PWA_INSTALLED, true);
    this.isInstalled.set(true);
  }

  hasInstalledApp(): boolean {
    return this.isStandaloneMode() || !!this._storage.get(StoragePrefix.PWA_INSTALLED);
  }
}
